// math.ts — small column-major 4×4 / 3-vector helpers shared by the splat
// renderer, the orbit camera and the ray picker. The GL camera convention here
// is OpenGL's (camera looks down -Z, +Y up); the world frame is openscan's
// (metres, Z up, ARCHITECTURE.md §2). Everything is plain Float32Array/number
// tuples so the hot paths never allocate objects per gaussian.

export type Vec3 = [number, number, number];
export type Mat4 = Float32Array; // 16 floats, column-major (WebGL layout)

export function vec3Sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function vec3Add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function vec3Scale(a: Vec3, s: number): Vec3 {
  return [a[0] * s, a[1] * s, a[2] * s];
}

export function vec3Dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function vec3Cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

export function vec3Len(a: Vec3): number {
  return Math.hypot(a[0], a[1], a[2]);
}

export function vec3Normalize(a: Vec3): Vec3 {
  const n = vec3Len(a);
  return n > 0 ? [a[0] / n, a[1] / n, a[2] / n] : [0, 0, 0];
}

export function vec3Dist(a: Vec3, b: Vec3): number {
  return vec3Len(vec3Sub(a, b));
}

export function mat4Identity(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

// mat4Multiply returns a·b (b applied first).
export function mat4Multiply(a: Mat4, b: Mat4): Mat4 {
  const out = new Float32Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
      out[c * 4 + r] = s;
    }
  }
  return out;
}

// mat4Perspective is the standard GL projection (fovy in radians).
export function mat4Perspective(fovy: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovy / 2);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) / (near - far);
  m[11] = -1;
  m[14] = (2 * far * near) / (near - far);
  return m;
}

// mat4LookAt builds a world→view matrix with the camera at eye looking at
// target; `up` is the world up hint (openscan: +Z).
export function mat4LookAt(eye: Vec3, target: Vec3, up: Vec3): Mat4 {
  const zAxis = vec3Normalize(vec3Sub(eye, target)); // camera +Z points backwards
  let xAxis = vec3Cross(up, zAxis);
  if (vec3Len(xAxis) < 1e-9) xAxis = vec3Cross([0, 1, 0], zAxis);
  xAxis = vec3Normalize(xAxis);
  const yAxis = vec3Cross(zAxis, xAxis);
  const m = new Float32Array(16);
  m[0] = xAxis[0]; m[4] = xAxis[1]; m[8] = xAxis[2];
  m[1] = yAxis[0]; m[5] = yAxis[1]; m[9] = yAxis[2];
  m[2] = zAxis[0]; m[6] = zAxis[1]; m[10] = zAxis[2];
  m[12] = -vec3Dot(xAxis, eye);
  m[13] = -vec3Dot(yAxis, eye);
  m[14] = -vec3Dot(zAxis, eye);
  m[15] = 1;
  return m;
}

// mat4Invert returns the inverse, or null when the matrix is singular.
export function mat4Invert(m: Mat4): Mat4 | null {
  const inv = new Float32Array(16);
  inv[0] = m[5] * m[10] * m[15] - m[5] * m[11] * m[14] - m[9] * m[6] * m[15] + m[9] * m[7] * m[14] + m[13] * m[6] * m[11] - m[13] * m[7] * m[10];
  inv[4] = -m[4] * m[10] * m[15] + m[4] * m[11] * m[14] + m[8] * m[6] * m[15] - m[8] * m[7] * m[14] - m[12] * m[6] * m[11] + m[12] * m[7] * m[10];
  inv[8] = m[4] * m[9] * m[15] - m[4] * m[11] * m[13] - m[8] * m[5] * m[15] + m[8] * m[7] * m[13] + m[12] * m[5] * m[11] - m[12] * m[7] * m[9];
  inv[12] = -m[4] * m[9] * m[14] + m[4] * m[10] * m[13] + m[8] * m[5] * m[14] - m[8] * m[6] * m[13] - m[12] * m[5] * m[10] + m[12] * m[6] * m[9];
  inv[1] = -m[1] * m[10] * m[15] + m[1] * m[11] * m[14] + m[9] * m[2] * m[15] - m[9] * m[3] * m[14] - m[13] * m[2] * m[11] + m[13] * m[3] * m[10];
  inv[5] = m[0] * m[10] * m[15] - m[0] * m[11] * m[14] - m[8] * m[2] * m[15] + m[8] * m[3] * m[14] + m[12] * m[2] * m[11] - m[12] * m[3] * m[10];
  inv[9] = -m[0] * m[9] * m[15] + m[0] * m[11] * m[13] + m[8] * m[1] * m[15] - m[8] * m[3] * m[13] - m[12] * m[1] * m[11] + m[12] * m[3] * m[9];
  inv[13] = m[0] * m[9] * m[14] - m[0] * m[10] * m[13] - m[8] * m[1] * m[14] + m[8] * m[2] * m[13] + m[12] * m[1] * m[10] - m[12] * m[2] * m[9];
  inv[2] = m[1] * m[6] * m[15] - m[1] * m[7] * m[14] - m[5] * m[2] * m[15] + m[5] * m[3] * m[14] + m[13] * m[2] * m[7] - m[13] * m[3] * m[6];
  inv[6] = -m[0] * m[6] * m[15] + m[0] * m[7] * m[14] + m[4] * m[2] * m[15] - m[4] * m[3] * m[14] - m[12] * m[2] * m[7] + m[12] * m[3] * m[6];
  inv[10] = m[0] * m[5] * m[15] - m[0] * m[7] * m[13] - m[4] * m[1] * m[15] + m[4] * m[3] * m[13] + m[12] * m[1] * m[7] - m[12] * m[3] * m[5];
  inv[14] = -m[0] * m[5] * m[14] + m[0] * m[6] * m[13] + m[4] * m[1] * m[14] - m[4] * m[2] * m[13] - m[12] * m[1] * m[6] + m[12] * m[2] * m[5];
  inv[3] = -m[1] * m[6] * m[11] + m[1] * m[7] * m[10] + m[5] * m[2] * m[11] - m[5] * m[3] * m[10] - m[9] * m[2] * m[7] + m[9] * m[3] * m[6];
  inv[7] = m[0] * m[6] * m[11] - m[0] * m[7] * m[10] - m[4] * m[2] * m[11] + m[4] * m[3] * m[10] + m[8] * m[2] * m[7] - m[8] * m[3] * m[6];
  inv[11] = -m[0] * m[5] * m[11] + m[0] * m[7] * m[9] + m[4] * m[1] * m[11] - m[4] * m[3] * m[9] - m[8] * m[1] * m[7] + m[8] * m[3] * m[5];
  inv[15] = m[0] * m[5] * m[10] - m[0] * m[6] * m[9] - m[4] * m[1] * m[10] + m[4] * m[2] * m[9] + m[8] * m[1] * m[6] - m[8] * m[2] * m[5];
  const det = m[0] * inv[0] + m[1] * inv[4] + m[2] * inv[8] + m[3] * inv[12];
  if (Math.abs(det) < 1e-20) return null;
  for (let i = 0; i < 16; i++) inv[i] /= det;
  return inv;
}

// transformPoint applies m to (p,1) with the perspective divide.
export function transformPoint(m: Mat4, p: Vec3): Vec3 {
  const x = m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12];
  const y = m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13];
  const z = m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14];
  const w = m[3] * p[0] + m[7] * p[1] + m[11] * p[2] + m[15];
  return w !== 0 ? [x / w, y / w, z / w] : [x, y, z];
}

// projectToScreen maps a world point to CSS pixels (origin top-left). Returns
// null when the point is behind the camera.
export function projectToScreen(viewProj: Mat4, p: Vec3, width: number, height: number): [number, number] | null {
  const w = viewProj[3] * p[0] + viewProj[7] * p[1] + viewProj[11] * p[2] + viewProj[15];
  if (w <= 1e-6) return null;
  const x = (viewProj[0] * p[0] + viewProj[4] * p[1] + viewProj[8] * p[2] + viewProj[12]) / w;
  const y = (viewProj[1] * p[0] + viewProj[5] * p[1] + viewProj[9] * p[2] + viewProj[13]) / w;
  return [(x * 0.5 + 0.5) * width, (1 - (y * 0.5 + 0.5)) * height];
}

// quatToMat3 converts a [w,x,y,z] quaternion (normalized here) into a
// row-major 3×3 rotation.
export function quatToMat3(w: number, x: number, y: number, z: number): number[] {
  const n = Math.hypot(w, x, y, z) || 1;
  w /= n; x /= n; y /= n; z /= n;
  return [
    1 - 2 * (y * y + z * z), 2 * (x * y - w * z), 2 * (x * z + w * y),
    2 * (x * y + w * z), 1 - 2 * (x * x + z * z), 2 * (y * z - w * x),
    2 * (x * z - w * y), 2 * (y * z + w * x), 1 - 2 * (x * x + y * y),
  ];
}

// covariance3D returns the upper triangle [xx,xy,xz,yy,yz,zz] of
// Σ = R·S·Sᵀ·Rᵀ for one gaussian (S = diag(exp(logScale))).
export function covariance3D(logScale: ArrayLike<number>, rot: ArrayLike<number>, out: Float32Array, o: number): void {
  const r = quatToMat3(rot[0], rot[1], rot[2], rot[3]);
  const sx = Math.exp(logScale[0]), sy = Math.exp(logScale[1]), sz = Math.exp(logScale[2]);
  // M = R·S (columns of R scaled), Σ = M·Mᵀ.
  const m = [
    r[0] * sx, r[1] * sy, r[2] * sz,
    r[3] * sx, r[4] * sy, r[5] * sz,
    r[6] * sx, r[7] * sy, r[8] * sz,
  ];
  out[o] = m[0] * m[0] + m[1] * m[1] + m[2] * m[2];
  out[o + 1] = m[0] * m[3] + m[1] * m[4] + m[2] * m[5];
  out[o + 2] = m[0] * m[6] + m[1] * m[7] + m[2] * m[8];
  out[o + 3] = m[3] * m[3] + m[4] * m[4] + m[5] * m[5];
  out[o + 4] = m[3] * m[6] + m[4] * m[7] + m[5] * m[8];
  out[o + 5] = m[6] * m[6] + m[7] * m[7] + m[8] * m[8];
}

// poseCameraCenter returns the world position of a world→camera pose
// (Xc = R·Xw + T, R row-major): C = -Rᵀ·T.
export function poseCameraCenter(R: ArrayLike<number>, T: ArrayLike<number>): Vec3 {
  return [
    -(R[0] * T[0] + R[3] * T[1] + R[6] * T[2]),
    -(R[1] * T[0] + R[4] * T[1] + R[7] * T[2]),
    -(R[2] * T[0] + R[5] * T[1] + R[8] * T[2]),
  ];
}
